import { useState, useRef, useImperativeHandle, forwardRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import BaseModal from '@/component/modal/BaseModal';
import SolidButton from '@/component/button/SolidButton';
import BorderButton from '@/component/button/BorderButton';
import { toast } from 'react-toastify';
import {
  useStartMindHealthSession,
  useCancelMindHealthSession,
  useGetMindHealthSession,
  useGetMindHealthSessions,
  SessionStatus
} from '@/api/mentalHealth/mindHealthSession';
import { useMindHealthSessionStore } from '@/store/MindHealthSessionStore';
import { useQueryGetMindHealthReport } from '@/api/mentalHealth/mindHealthReport';
import { useAppLocale } from '@/locales/locale';

interface BCGSessionModalProps {
  userId?: number;
  userName?: string;
  onSuccess?: () => void;
}

export interface BCGSessionModalRef {
  show: () => void;
  close: () => void;
}

const BCGSessionModal = forwardRef<BCGSessionModalRef, BCGSessionModalProps>(({
  userId,
  userName,
  onSuccess,
}, ref) => {
  const { t } = useTranslation();
  const { intlLocale } = useAppLocale();
  const modalRef = useRef<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Store
  const {
    currentSession,
    isSessionActive,
    setCurrentSession,
    clearSession,
    getProgress,
    isSessionCompleted,
  } = useMindHealthSessionStore();

  // 세션이 COMPLETED 가 되면 서버가 리포트 계산을 큐에 넣는다. 그 세션을 폴링한다.
  const [completedSessionId, setCompletedSessionId] = useState<string | null>(null);
  const [isAnalysisTimedOut, setIsAnalysisTimedOut] = useState(false);

  // API Mutations
  const startSessionMutation = useStartMindHealthSession();
  const cancelSessionMutation = useCancelMindHealthSession();

  // 진행 중인 세션 확인 (모달이 열릴 때) - INITIALIZING, PENDING_DEVICE_WAKEUP, IN_PROGRESS 모두 확인
  const { data: activeInProgressData } = useGetMindHealthSessions(
    userId || 0,
    { status: 'IN_PROGRESS', pageSize: 1 },
    isModalOpen && !!userId && !currentSession
  );
  const { data: activeInitializingData } = useGetMindHealthSessions(
    userId || 0,
    { status: 'INITIALIZING', pageSize: 1 },
    isModalOpen && !!userId && !currentSession
  );
  const { data: activePendingWakeupData } = useGetMindHealthSessions(
    userId || 0,
    { status: 'PENDING_DEVICE_WAKEUP', pageSize: 1 },
    isModalOpen && !!userId && !currentSession
  );

  // 진행 중인 세션이 있으면 자동으로 복구 (어떤 활성 상태든)
  useEffect(() => {
    const activeSession =
      activeInProgressData?.data?.[0] ||
      activePendingWakeupData?.data?.[0] ||
      activeInitializingData?.data?.[0];
    if (activeSession) {
      setCurrentSession(activeSession);
    }
  }, [activeInProgressData, activeInitializingData, activePendingWakeupData]);

  // 세션 상태 폴링 (INITIALIZING, PENDING_DEVICE_WAKEUP, IN_PROGRESS일 때)
  // PENDING_DEVICE_WAKEUP 상태에서는 백엔드가 1분마다 ping → 5초 간격으로 폴링
  // INITIALIZING/IN_PROGRESS 상태에서는 2초 간격으로 폴링
  const pollingInterval = currentSession?.status === 'PENDING_DEVICE_WAKEUP' ? 5000 : 2000;
  const { data: sessionData } = useGetMindHealthSession(
    userId || 0,
    currentSession?.sessionId || '',
    !!currentSession && isSessionActive,
    pollingInterval
  );

  // 세션 데이터가 업데이트되면 Store 업데이트
  useEffect(() => {
    if (sessionData) {
      setCurrentSession(sessionData);

      // 완료되면 리포트 계산을 기다린다. 브라우저는 더 이상 아무것도 계산하지 않는다.
      if (sessionData.status === 'COMPLETED') {
        setCompletedSessionId(sessionData.sessionId);
      }

      // 에러 상태 처리
      if (isErrorStatus(sessionData.status)) {
        handleSessionError(sessionData.status, sessionData.notes);
      }
    }
  }, [sessionData]);

  useImperativeHandle(ref, () => ({
    show: () => {
      setIsModalOpen(true);
      modalRef.current?.show();
    },
    close: () => {
      setIsModalOpen(false);
      modalRef.current?.close();
    },
  }));

  // 세션 시작 - 백엔드가 즉시 201 반환 (INITIALIZING 상태), 이후 폴링으로 상태 확인
  const handleStartSession = async () => {
    if (!userId) {
      toast.error(t('mentalHealth.session.error.noUser'));
      return;
    }

    try {
      const result = await startSessionMutation.mutateAsync(userId);
      // 즉시 INITIALIZING 상태로 응답 → 폴링이 자동으로 상태 변화를 감지
      setCurrentSession(result.data);
      toast.success(t('mentalHealth.session.started'));
    } catch (error: any) {
      // 에러 메시지 처리 (503은 더 이상 발생하지 않음 → PENDING_DEVICE_WAKEUP 상태로 전환됨)
      const errorMessage = error?.response?.data?.message || error?.message;
      if (error?.response?.status === 409) {
        toast.error(t('mentalHealth.session.error.alreadyInProgress'));
      } else {
        toast.error(t('mentalHealth.session.error.startFailed', { message: errorMessage }));
      }
    }
  };

  // 세션 취소
  const handleCancelSession = async () => {
    if (!userId || !currentSession) return;

    try {
      await cancelSessionMutation.mutateAsync({
        protecteeId: userId,
        sessionId: currentSession.sessionId,
      });
      toast.info(t('mentalHealth.session.cancelled'));
      clearSession();
    } catch (error) {
      // console.error('세션 취소 실패:', error);
      toast.error(t('mentalHealth.session.error.cancelFailed'));
    }
  };

  // 리포트 계산 대기.
  //
  // 조회 자체가 큐를 깨우므로(계산 전이면 404 가 아니라 PENDING 이 온다) 재계산을
  // 반복 호출하지 않고 폴링만 한다. 분석은 워커에서 도는 수 초짜리 CPU 작업이다.
  const { data: reportResult } = useQueryGetMindHealthReport(
    userId || 0,
    completedSessionId,
    !!completedSessionId,
    true
  );

  // 큐가 비어 있거나 워커가 느릴 수 있다. 상한 없이 기다리면 닫히지 않는 모달이 된다.
  useEffect(() => {
    if (!completedSessionId) {
      setIsAnalysisTimedOut(false);
      return;
    }

    const timer = setTimeout(() => setIsAnalysisTimedOut(true), 2 * 60 * 1000);
    return () => clearTimeout(timer);
  }, [completedSessionId]);

  useEffect(() => {
    if (!completedSessionId) return;

    if (isAnalysisTimedOut && reportResult?.status === 'PENDING') {
      toast.info(t('mentalHealth.session.analysisQueued'));
      onSuccess?.();
      handleCloseModal();
      return;
    }

    if (!reportResult || reportResult.status === 'PENDING') return;

    if (reportResult.status === 'SUCCEEDED') {
      toast.success(t('mentalHealth.session.analysisComplete'));
    } else if (reportResult.status === 'INSUFFICIENT_DATA') {
      toast.warning(t('mentalHealth.session.error.insufficientData'));
    } else {
      toast.error(t('mentalHealth.session.error.analysisFailed'));
    }

    onSuccess?.();
    handleCloseModal();
  }, [reportResult, completedSessionId, isAnalysisTimedOut]);

  // 에러 상태 확인
  const isErrorStatus = (status: SessionStatus) => {
    return ['FAILED', 'ABORTED_TIMEOUT', 'ABORTED_OFFLINE', 'ABORTED_UNEXPECTED'].includes(status);
  };

  // 에러 처리
  const handleSessionError = (status: SessionStatus, notes: string | null) => {
    let errorMessage = '';

    switch (status) {
      case 'ABORTED_TIMEOUT':
        errorMessage = t('mentalHealth.session.error.timeout');
        break;
      case 'ABORTED_OFFLINE':
        // PENDING_DEVICE_WAKEUP 대기 만료로 인한 오프라인 종료
        errorMessage = t('mentalHealth.session.error.deviceWakeupFailed');
        break;
      case 'ABORTED_UNEXPECTED':
        errorMessage = t('mentalHealth.session.error.serverError');
        break;
      case 'FAILED':
        errorMessage = notes || t('mentalHealth.session.error.failed');
        break;
    }

    toast.error(errorMessage);
    clearSession();
  };

  // 모달 닫기 요청 (버튼 클릭 등)
  const handleCloseModal = () => {
    setIsModalOpen(false);
    modalRef.current?.close();
  };

  // 모달이 실제로 닫힐 때 호출되는 콜백 (BaseModal의 onCloseCallback)
  const handleModalClosed = () => {
    setIsModalOpen(false);
    clearSession();
    setCompletedSessionId(null);
  };

  // 상태별 메시지
  const getStatusMessage = () => {
    // 수집이 끝나고 서버가 계산하는 구간. 예전에는 이 자리에서 브라우저가 직접 돌았다.
    if (completedSessionId && reportResult?.status === 'PENDING') {
      return t('mentalHealth.session.analyzing');
    }

    if (!currentSession) return '';

    switch (currentSession.status) {
      case 'INITIALIZING':
        return t('mentalHealth.session.status.initializing');
      case 'PENDING_DEVICE_WAKEUP':
        return t('mentalHealth.session.status.pendingDeviceWakeup');
      case 'IN_PROGRESS':
        return t('mentalHealth.session.status.inProgress');
      case 'COMPLETED':
        return t('mentalHealth.session.status.completed');
      case 'CANCELLED':
        return t('mentalHealth.session.status.cancelled');
      default:
        return '';
    }
  };

  // 상태별 아이콘
  const getStatusIcon = () => {
    if (!currentSession) return '🔄';

    switch (currentSession.status) {
      case 'INITIALIZING':
        return '🔄';
      case 'PENDING_DEVICE_WAKEUP':
        return '📡';
      case 'IN_PROGRESS':
        return '📊';
      case 'COMPLETED':
        return '✅';
      case 'CANCELLED':
        return '❌';
      default:
        return '⚠️';
    }
  };

  return (
    <BaseModal ref={modalRef} onCloseCallback={handleModalClosed} width="600" showCloseButton={false}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-bold">
              {t('mentalHealth.session.title')}
            </h2>
            {userName && (
              <p className="text-sm text-gray-600 mt-1">
                {t('mentalHealth.modal.targetMember')}: <strong>{userName}</strong>
              </p>
            )}
          </div>
          <button
            onClick={handleCloseModal}
            className="text-gray-400 hover:text-gray-600 text-2xl"
            disabled={isSessionActive}
          >
            ×
          </button>
        </div>

        {!currentSession ? (
          // 세션 시작 전
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-800 mb-2">
                {t('mentalHealth.session.info.title')}
              </h3>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• {t('mentalHealth.session.info.duration')}</li>
                <li>• {t('mentalHealth.session.info.device')}</li>
                <li>• {t('mentalHealth.session.info.cancel')}</li>
              </ul>
            </div>

            <div className="flex justify-end space-x-3">
              <BorderButton
                text={t('mentalHealth.modal.button.cancel')}
                onClick={handleCloseModal}
              />
              <SolidButton
                text={t('mentalHealth.session.button.start')}
                onClick={handleStartSession}
                disabled={startSessionMutation.isPending}
              />
            </div>
          </div>
        ) : (
          // 세션 진행 중
          <div className="space-y-6">
            {/* 상태 표시 */}
            <div className="text-center py-8">
              <div className="text-6xl mb-4 animate-pulse">{getStatusIcon()}</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {getStatusMessage()}
              </h3>

              {/* PENDING_DEVICE_WAKEUP 상태: 디바이스 대기 안내 */}
              {currentSession.status === 'PENDING_DEVICE_WAKEUP' && (
                <div className="mt-4 mx-auto max-w-md">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left">
                    <p className="text-sm text-amber-800 font-medium mb-2">
                      {t('mentalHealth.session.status.pendingDeviceWakeupDesc')}
                    </p>
                    <ul className="text-xs text-amber-700 space-y-1">
                      <li>• {t('mentalHealth.session.status.pendingDeviceWakeupRetry')}</li>
                      <li>• {t('mentalHealth.session.status.pendingDeviceWakeupTimeout')}</li>
                      <li>• {t('mentalHealth.session.status.pendingDeviceWakeupCancel')}</li>
                    </ul>
                  </div>
                  {/* 대기 중 애니메이션 바 */}
                  <div className="mt-4 mb-2">
                    <div className="w-full bg-amber-100 rounded-full h-2 overflow-hidden">
                      <div className="bg-amber-500 h-2 rounded-full animate-pulse" style={{ width: '100%' }} />
                    </div>
                  </div>
                </div>
              )}

              {currentSession.status === 'IN_PROGRESS' && (
                <>
                  <div className="mt-6 mb-2">
                    <div className="w-full bg-gray-200 rounded-full h-4">
                      <div
                        className="bg-emerald-600 h-4 rounded-full transition-all duration-500"
                        style={{ width: `${getProgress()}%` }}
                      />
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">
                    {t('mentalHealth.session.progress', {
                      received: currentSession.receivedChunkCount,
                      total: currentSession.expectedTotalChunks,
                      percent: getProgress()
                    })}
                  </p>
                </>
              )}
            </div>

            {/* 세션 정보 */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">{t('mentalHealth.session.sessionId')}:</span>
                <span className="font-mono text-xs">{currentSession.sessionId}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t('mentalHealth.session.startTime')}:</span>
                <span>{new Date(currentSession.startTime).toLocaleString(intlLocale)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t('mentalHealth.session.deviceId')}:</span>
                <span>{currentSession.deviceId}</span>
              </div>
            </div>

            {/* 버튼 */}
            <div className="flex justify-end space-x-3">
              {isSessionActive && (
                <button
                  onClick={handleCancelSession}
                  disabled={cancelSessionMutation.isPending}
                  className="px-6 py-3 border-2 border-red-500 text-red-500 hover:bg-red-50 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('mentalHealth.session.button.cancel')}
                </button>
              )}
              {isSessionCompleted() && (
                <SolidButton
                  text={t('mentalHealth.modal.button.done')}
                  onClick={handleCloseModal}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </BaseModal>
  );
});

export default BCGSessionModal;
